import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  findAgentLogListItem,
  resolveAgentLogListItem
} from './resolveAgentLogListItem';
import type { AgentLogsQueryParams } from '../mapAgentLogsFiltersToQueryParams';

import { agentLogsQueryKeys, getAgentExecutionDetail } from '~/api/agentLogs';
import { AgentExecutionNotFoundError } from '~/api/agentLogs/errors';
import type {
  AgentExecutionDetail,
  AgentExecutionListItem
} from '~/api/agentLogs/types';

type UseAgentLogDetailParams = {
  realm: string;
  environment: string;
  agentExecutionKey: string | null;
  queryParams: AgentLogsQueryParams;
  listItems: AgentExecutionListItem[];
  enabled?: boolean;
};

export function useAgentLogDetail({
  realm,
  environment,
  agentExecutionKey,
  queryParams,
  listItems,
  enabled = true
}: UseAgentLogDetailParams) {
  const listItem = useMemo(
    () =>
      agentExecutionKey
        ? findAgentLogListItem(listItems, agentExecutionKey)
        : undefined,
    [listItems, agentExecutionKey]
  );

  const queryEnabled =
    enabled && !!realm && !!environment && !!agentExecutionKey;

  return useQuery<AgentExecutionDetail>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- key is stable per execution; list context is read inside queryFn
    queryKey: agentLogsQueryKeys.detailByExecutionKey({
      realm,
      environment,
      agentExecutionKey: agentExecutionKey ?? ''
    }),
    queryFn: async () => {
      if (!agentExecutionKey) {
        throw new Error('agentExecutionKey is required');
      }

      const resolved =
        listItem ??
        (await resolveAgentLogListItem({
          agentExecutionKey,
          listItems,
          realm,
          environment,
          queryParams
        }));

      if (!resolved) {
        throw new AgentExecutionNotFoundError(agentExecutionKey);
      }

      return getAgentExecutionDetail({
        realm,
        environment,
        agentExecutionKey,
        connectorId: resolved.connectorId,
        pipelineName: resolved.pipelineName,
        pipelineVersion: resolved.pipelineVersion,
        pipelineKey: resolved.pipelineKey,
        agentName: resolved.agentName
      });
    },
    enabled: queryEnabled,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false
  });
}
